/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, November 11th 2021, 10:40:23 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import {
  useForm,
  UseFormReturn,
  SubmitHandler,
  SubmitErrorHandler,
  FieldErrors,
  DefaultValues,
  UnpackNestedValue,
} from "react-hook-form";
import { ValidationResolver, schemaBuilder } from "lib/validation";
import { APIServicePathType, ObjectKey, ObjectValue, PrimaryKeyValue, RequestMethodType } from "lib/types";
import { useAPIMutation, useAPIRequest } from "lib/api";
import { APIRequestState } from "lib/api/types";
import { isEmpty } from "lodash";
import { APIDataParser } from "lib/api/parsers/APIDataParser";
import { logger } from "lib/logger";
import { parsePrimaryKeyValue } from "lib/utils";

/**
 *
 * @param apiServicePath The API service e.g. /users/
 * @param apiFormDefaultValues The default values of the form. If it is stateful, it will cause a form reset when the primary key is part of the object and it changes.
 * @param apiFormDataValidationSchema The validation schema for the data being posted.
 * @param setAPIFormResponse The state dispatch function on the calling component that needs its
 * @param apiInitialFetchId The primary key value i.e. id for fetching data that will prefill the form. This value can be null when you dont want the hook to fetch initial data.
 * @param primaryKey The primary key of the data if it is persistent data i.e. model instance. If it is not
 * persistent data, you can provide the value of this argument as null. So this is the primary key field of the passed
 * object e.g. 'id'. This parameter can be null when data that is submitted with this hook will have no primary key field e.g. login credentials etc.
 * @param titleKey The attribute that references the name of the object e.g. title, name, heading etc.
 * @param errorNotificationMessage: A custom message for when an API error occurs on sending data.
 * @param successNotificationMessage A custom message to display on successful saving of data on the backend.
 * @param apiFormActionType What the form is supposed to do on a high level. i.e. whether it updates a model instance on the backend api or just does an action that needs some backend processing e.g. installing a service, testing mail server settings etc.
 * @param method An explicit unsafe method to use. This only really applies for case where an existing model instance is being updated. so one should choose between PUT and PATCH
 * @param apiPathParams any path parameters to add to the api end point that posts to the backend
 * @param apiQueryParams any query parameters to add to the api end point that posts to the backend
 * @param onValidCallbackFn A callback function for when the data is valid. This will be called before or as the data is being sent to the API
 * @param onInvalidCallbackFn A callback function for when the data is invalid.
 * state to be updated by the API response. This useAPIForm hook is agnostic to how the state dispatch
 * call function is implement so you can use react usestate, usereducer, redux dispatch action etc.
 *
 *
 */

/**
 * APIFormActionType:
 * create_update_single_object: Denotes that the action creates or updates an object/model instance in the backend
 * generic_api_action: Does not really create/update an object but carries out an action e.g. sends a notification, installs a service etc. These actions
 * typically do not result in a single model instance being created/update but rather do an action that could or could not lead to more than one model
 * instances being created or updated.
 */
type APIFormActionType = "create_update_single_object" | "generic_api_action";

export interface UseAPIFormSpec<TAPIFormData, TAPIResponse = TAPIFormData> {
  apiServicePath: APIServicePathType;
  apiFormDefaultValues: TAPIFormData; // Contextual type inferencing will be done based on the type of the apiFormDefaultValues provided to this hook and any other hook that extends it.
  apiFormDataValidationSchema: schemaBuilder.AnyObjectSchema; // TODO: Figure out how to make schemaBuilder.SchemaOf<T> Work here. There is a major issue with Yup and typescript inference - https://github.com/jquense/yup/issues/916
  primaryKey: ObjectKey<TAPIFormData> | null;
  apiInitialFetchId: PrimaryKeyValue<TAPIResponse> | null;
  setAPIFormResponse?: React.Dispatch<React.SetStateAction<TAPIResponse>>; // This is callBack is not required because a form may just need to send data to the api with out the need to reset the form state e.g. a login form just posts the credentials and moves on.
  onSaveAPIItem?: (item: TAPIResponse) => void;
  titleKey?: ObjectKey<TAPIResponse>;
  errorNotificationMessage?: string;
  successNotificationMessage?: string;
  apiFormActionType?: APIFormActionType;
  method?: Extract<RequestMethodType, "POST" | "PATCH" | "PUT">;
  apiPathParams?: any[];
  apiQueryParams?: { [key: string]: string | number | boolean };
  onValidCallbackFn?: SubmitHandler<any>; // Making onValid optional so we can use our default value and call a callback.
  onInvalidCallbackFn?: SubmitErrorHandler<TAPIFormData>;
  formResetDependencyArray?: ObjectValue<TAPIFormData>[];
  fieldsToResetOnFormReset?: (keyof TAPIFormData)[];
}

interface UseInitialAPIFormDataSpec<TAPIFormData, TAPIResponse = TAPIFormData>
  extends Pick<
    UseAPIFormSpec<TAPIFormData, TAPIResponse>,
    | "apiServicePath"
    | "apiInitialFetchId"
    | "primaryKey"
    | "setAPIFormResponse"
    | "apiFormActionType"
    | "apiQueryParams"
  > {
  reset: UseFormReturn<TAPIFormData>["reset"];
}

interface UseInitialAPIFormDataReturn {
  editFetchHasError: boolean;
  editFetchIsSuccessful: boolean;
  isAPIFetchingEditItem: boolean;
  apiEditFetchErrors: { [key: string]: any };
  editFetchHTTPStatusCode: number;
}

export interface UseAPIFormReturn<TAPIFormData, TAPIResponse = TAPIFormData>
  extends UseInitialAPIFormDataReturn,
    Pick<UseAPIFormSpec<TAPIFormData, TAPIResponse>, "apiFormDataValidationSchema"> {
  useFormReturn: UseFormReturn<TAPIFormData>;
  apiObjectId: UseAPIFormSpec<TAPIFormData, TAPIResponse>["apiInitialFetchId"];
  isSendingDataToAPI: boolean;
  isSuccessfulAPIDataSend: boolean;
  isAPIDataSendError: boolean;
  isAPIDataUpdate: boolean;
  apiDataSendErrors: APIRequestState["apiErrorState"]["apiErrors"];
  apiDataSendResponseCode: APIRequestState["httpResponseStatusCode"];
  apiDataSendRequestCount: number;
  successMessage: string;
  errorMessage: string;
  submitAPIForm: (e?: React.BaseSyntheticEvent) => Promise<void>;
}

/**
 *
 * Hook for API form. Uses POST method and the idempotent methods i.e. PUT and PATCH.
 * Only sends data but does not fetch and auto-populate form for existing data.
 *
 */

export const useAPIForm = <TAPIFormData, TAPIResponse = TAPIFormData>(
  spec: UseAPIFormSpec<TAPIFormData, TAPIResponse>
): UseAPIFormReturn<TAPIFormData, TAPIResponse> => {
  const {
    apiFormDefaultValues,
    apiFormDataValidationSchema,
    apiServicePath,
    setAPIFormResponse,
    onSaveAPIItem,
    primaryKey,
    titleKey,
    errorNotificationMessage = null,
    successNotificationMessage = null,
    apiInitialFetchId,
    apiFormActionType = "create_update_single_object",
    method = null,
    apiPathParams = [],
    apiQueryParams = {},
    formResetDependencyArray = [],
    fieldsToResetOnFormReset = [],
  } = spec;

  // TODO: Add a type generic for apiFormDataValidationSchema
  // https://github.com/react-hook-form/react-hook-form/issues/2129
  const useFormReturn = useForm<TAPIFormData>({
    defaultValues: apiFormDefaultValues as DefaultValues<TAPIFormData>,
    mode: "onTouched",
    resolver: ValidationResolver(apiFormDataValidationSchema),
  });

  const { reset, getValues } = useFormReturn;

  // Get the primary key from the default values if set. If not set in default values, get it from
  // apiInitialFetchId.
  // Ideally the calling code should ensure apiFormDefaultValues and apiInitialFetchId are in sync
  // But in case they are not, we give a higher priority for the pk in default values if provided.
  const apiObjectId: PrimaryKeyValue<TAPIFormData> =
    (apiFormDefaultValues?.[primaryKey] as PrimaryKeyValue<TAPIFormData>) ?? apiInitialFetchId;

  const [formResetSignal, setFormResetSignal] = React.useState<number>(0);

  const [defaultSuccessMessage, setDefaultSuccessMessage] = React.useState("");

  const resetForm = (defaultValues: TAPIFormData = apiFormDefaultValues) => {
    if (!isEmpty(defaultValues)) reset(defaultValues as DefaultValues<TAPIFormData>);

    if (isEmpty(defaultValues)) reset({} as DefaultValues<TAPIFormData>);
  };

  React.useEffect(() => {
    if (!formResetSignal) return;

    if (fieldsToResetOnFormReset.length > 0) {
      // Not using resetField because it re-evaluates isDirty and isValid (https://react-hook-form.com/docs/useform/resetfield)
      //  which is not necessary here and may cause unnecessary re-renders / re-submissions of the form.
      const resetValues = fieldsToResetOnFormReset.reduce((acc, field) => {
        acc[field] = apiFormDefaultValues[field];
        return acc;
      }, {} as TAPIFormData);

      resetForm({ ...getValues(), ...resetValues });

      return;
    }

    resetForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formResetSignal, ...fieldsToResetOnFormReset]);

  useFormResetDependencyArray<TAPIFormData>(setFormResetSignal, formResetDependencyArray);

  const {
    isMakingAPIRequest: isSendingDataToAPI,
    isSuccessfulAPIRequest: isSuccessfulAPIDataSend,
    isAPIError: isAPIDataSendError,
    apiErrors: apiDataSendErrors,
    httpResponseStatusCode: apiDataSendResponseCode,
    apiRequestCount: apiDataSendRequestCount,
    postData,
    putData,
    patchData,
    isAPIDataUpdate, // Always true for idempotent http methods
  } = useAPIMutation<TAPIResponse>({ apiServicePath });

  const handleValidForm: SubmitHandler<TAPIFormData> = (data: UnpackNestedValue<TAPIFormData>) => {
    const { parseAPIData } = APIDataParser(apiFormDataValidationSchema);

    const { headers, parsedData } = parseAPIData(data);

    sendParsedData(parsedData, headers);
  };

  // if there is ever a need to resetOnSaveAPIResponse, here is where to do it but have a prop (resetOnSaveAPIResponse)
  // that will be used to check whether to reset or not, so it cannot clash with other reset logic where not expected e.g.
  // a save response does a data change call back, this may result in a reset if the state being changed up the tree
  // is also set as apiFormDefaultValues
  const setValidAPIResponse = (apiDataSendResponse: TAPIResponse) => {
    setAPIFormResponse?.(apiDataSendResponse);
    onSaveAPIItem?.(apiDataSendResponse);
    setDefaultSuccessMessage(
      `${apiDataSendResponse?.[titleKey] ?? "Item"} was successfully ${apiObjectId ? "updated" : "created"}`
    );
  };

  const sendParsedData = (parsedData: BodyInit, headers: { [key: string]: string }) => {
    // Handle generic PUT/PATCH methods that dont necessarily update/save a model instance but rather, trigger some
    // action on the backend.

    switch (apiFormActionType) {
      case "generic_api_action":
        const api_request_args = {
          apiPathParams,
          apiQueryParams,
          body: parsedData,
          headers,
          setValidAPIResponse,
        };

        switch (method) {
          case "PATCH":
            patchData(api_request_args);
            return;
          case "PUT":
            putData(api_request_args);
            return;
          case "POST":
            postData(api_request_args);
            return;
        }

        return;

      case "create_update_single_object":
        if (apiObjectId) {
          // If apiObjectId is available, the default will be to send a PUT unless PATCH is expliclity stated
          method === "PATCH"
            ? patchData({
                apiPathParams: [apiObjectId],
                apiQueryParams,
                body: parsedData,
                headers,
                setValidAPIResponse,
              })
            : putData({ apiPathParams: [apiObjectId], apiQueryParams, body: parsedData, headers, setValidAPIResponse });
          return;
        }

        postData({ apiPathParams, apiQueryParams, body: parsedData, headers, setValidAPIResponse }); // The fallback will always be to send a POST request.
    }
  };

  const handleInvalidForm: SubmitErrorHandler<TAPIFormData> = (errors: FieldErrors<TAPIFormData>) => {
    //TODO: Do something here for invalid form.
    logger.error("API Form Validation Errors: ", errors);
  };

  const successMessage = React.useMemo(() => {
    if (!successNotificationMessage) {
      return defaultSuccessMessage;
    }
    return successNotificationMessage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successNotificationMessage, defaultSuccessMessage, isAPIDataUpdate]);

  let errorMessage = "";
  if (apiDataSendErrors?.detail) errorMessage += `${apiDataSendErrors.detail}. `;
  errorMessage +=
    errorNotificationMessage ??
    "An error occurred while trying to process your data. The errors have been highlighted for each field in the form.";

  const useInitialAPIFormDataReturn = useInitialAPIFormData<TAPIFormData, TAPIResponse>({
    apiServicePath,
    apiInitialFetchId,
    apiQueryParams,
    setAPIFormResponse,
    apiFormActionType,
    reset,
    primaryKey,
  });

  return {
    useFormReturn: useFormReturn,
    isSendingDataToAPI,
    isSuccessfulAPIDataSend,
    isAPIDataSendError,
    isAPIDataUpdate,
    apiDataSendErrors,
    apiDataSendResponseCode,
    successMessage,
    errorMessage,
    apiDataSendRequestCount,
    apiObjectId,
    ...useInitialAPIFormDataReturn,
    apiFormDataValidationSchema,
    submitAPIForm: useFormReturn.handleSubmit(handleValidForm, handleInvalidForm),
  };
};

/**
 *
 * Hook for API forms that handle mutable data i.e. the form can fetch initial API data, that resets the form values
 * with response data.
 * If an objectID is available, the form will fetch the item and auto-populate fields in form.
 *
 */

const useInitialAPIFormData = <TAPIFormData, TAPIResponse>(
  spec: UseInitialAPIFormDataSpec<TAPIFormData, TAPIResponse>
): UseInitialAPIFormDataReturn => {
  const {
    apiServicePath,
    apiInitialFetchId = null,
    setAPIFormResponse,
    apiFormActionType,
    reset,
    primaryKey,
    apiQueryParams,
  } = spec;

  const useAPIFetchItemReturn = useAPIRequest<TAPIResponse>({
    apiServicePath: apiServicePath,
    makeRequestOnInitialMount: false,
  });

  // reset form on initial fetch and call the setAPIFormResponse callback if provided
  const setValidAPIResponse = React.useCallback(
    (fetchedItem: TAPIResponse) => {
      const parsedInitialFetchId = apiInitialFetchId ? parsePrimaryKeyValue(apiInitialFetchId) : apiInitialFetchId;

      if (fetchedItem?.[primaryKey as any] === parsedInitialFetchId) {
        reset(fetchedItem as unknown as DefaultValues<TAPIFormData>);
      }

      setAPIFormResponse?.(fetchedItem);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiInitialFetchId, primaryKey]
  );

  // Fetching the item for an edit
  React.useEffect(() => {
    if (apiInitialFetchId && apiFormActionType === "create_update_single_object") {
      return useAPIFetchItemReturn.makeRequest({
        apiPathParams: [apiInitialFetchId],
        apiQueryParams,
        setValidAPIResponse,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiInitialFetchId]);

  return {
    editFetchHasError: useAPIFetchItemReturn.isAPIError,
    editFetchIsSuccessful: useAPIFetchItemReturn.isSuccessfulAPIRequest,
    isAPIFetchingEditItem: useAPIFetchItemReturn.isMakingAPIRequest,
    apiEditFetchErrors: useAPIFetchItemReturn.apiErrors,
    editFetchHTTPStatusCode: useAPIFetchItemReturn.httpResponseStatusCode,
  };
};

/**
 * Hook that resets form when values in dependency array are provided. A good use case to use
 * formRestDependencyArray is when this useAPIForm hook is being used among many different component
 * instance and you need to reset the form values as per each instance e.g. in a List of items that use
 * a shared form component.
 */
const useFormResetDependencyArray = <TAPIFormData>(
  setFormResetSignal: React.Dispatch<React.SetStateAction<number>>,
  formResetDependencyArray: ObjectValue<TAPIFormData>[]
) => {
  React.useEffect(() => {
    if (formResetDependencyArray.length > 0) setFormResetSignal((prev) => prev + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...formResetDependencyArray]);
};

// eslint-disable-next-line
const useResetOnSaveAPIResponse = () => {};
